// =============================================================================
// features/sales/campaigns/components/CampaignProgress.tsx — Barra de
// progresso da campanha (doc 26 §7 — componente nomeado `CampaignProgress`).
// Exportado para reuso em F35-S09 (detalhe) e F35-S10 (operação) — contrato
// de saída deste slot.
//
// `percent === null` representa "indisponível" (a API ainda não publica esse
// agregado, ver header de hooks/sales/useCampaigns.ts) — NUNCA um valor
// calculado no cliente a partir de dados parciais. A UI mostra uma trilha
// tracejada + rótulo textual em vez de fabricar um número.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../../lib/cn';

export interface CampaignProgressProps {
  /** 0–100, ou `null` quando o agregado ainda não está disponível na API. */
  percent: number | null;
  /** Rótulo textual complementar (ex.: "42 de 120 trabalhados"). */
  label?: string;
  className?: string;
}

export function CampaignProgress({
  percent,
  label,
  className,
}: CampaignProgressProps): React.JSX.Element {
  const clamped = percent === null ? null : Math.min(100, Math.max(0, Math.round(percent)));

  return (
    <div className={cn('flex flex-col gap-1 min-w-[120px]', className)}>
      <div
        className={cn(
          'h-2 w-full rounded-pill overflow-hidden',
          clamped === null ? 'border border-dashed border-border' : 'bg-surface-muted',
        )}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(clamped !== null ? { 'aria-valuenow': clamped } : {})}
        aria-label={clamped === null ? 'Progresso indisponível' : `${clamped}% trabalhado`}
      >
        {clamped !== null && (
          <div
            className="h-full rounded-pill transition-[width] duration-slow ease-out"
            style={{ width: `${clamped}%`, background: 'var(--brand-verde)' }}
          />
        )}
      </div>
      <span className="font-mono text-xs text-ink-3">
        {clamped === null ? 'Indisponível' : (label ?? `${clamped}%`)}
      </span>
    </div>
  );
}
